/* Product page: variant selection, gallery dots, lightbox, sticky bar, share. */

(() => {
  'use strict';

  class AntartiProduct extends HTMLElement {
    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-pdp-config]').textContent);
      this.picker = this.querySelector('[data-variant-picker]');
      this.form = this.querySelector('[data-product-form]');
      this.variants = this.picker ? JSON.parse(this.picker.querySelector('[data-variants-json]').textContent) : null;
      this.track = this.querySelector('[data-media-track]');
      this.cleanups = [];

      this.bindVariants();
      this.bindThemeSizes();
      this.bindGallery();
      this.bindLightbox();
      this.bindSticky();
      this.bindStickyColumn();
      this.bindVideos();
      this.bindShare();
      this.bindSizeGuide();
    }

    /* Scripted scrolls are instant for visitors who ask for reduced motion. */
    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /* ---------------- size guide drawer ---------------- */
    bindSizeGuide() {
      const dialog = this.querySelector('[data-size-guide-dialog]');
      if (!dialog) return;
      const body = dialog.querySelector('[data-size-guide-body]');
      let loaded = false;
      this.listen(this, 'click', async (e) => {
        if (e.target.closest('[data-size-guide-close]')) {
          dialog.close();
          return;
        }
        if (!e.target.closest('[data-size-guide-open]')) return;
        e.preventDefault();
        dialog.showModal();
        if (loaded) return;
        try {
          const res = await fetch(`${dialog.dataset.url}?section_id=size-guide&kind=${dialog.dataset.kind}`);
          if (!res.ok) throw new Error();
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          const guide = html.querySelector('antarti-size-guide');
          if (!guide) throw new Error();
          /* The guide opens on this tab (ring / bracelet / chain); the query string can't carry it. */
          guide.dataset.kind = dialog.dataset.kind;
          body.innerHTML = '';
          body.appendChild(guide);
          const css = html.querySelector('link[href*="section-size-guide"]');
          if (css && !document.querySelector('link[href*="section-size-guide"]')) document.head.appendChild(css.cloneNode());
          if (!customElements.get('antarti-size-guide')) {
            const script = html.querySelector('script[src*="section-size-guide"]');
            if (script) {
              const s = document.createElement('script');
              s.src = script.src;
              document.body.appendChild(s);
            }
          }
          loaded = true;
        } catch {
          body.innerHTML = `<a class="link" href="${dialog.dataset.url}">${dialog.getAttribute('aria-label')}</a>`;
        }
      });
    }

    disconnectedCallback() {
      this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
      this.io?.disconnect();
      this.stickyIo?.disconnect();
      this.stickyRo?.disconnect();
      this.videoIo?.disconnect();
      this.request?.abort();
      document.documentElement.style.scrollPaddingBottom = '';
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.cleanups.push(() => target.removeEventListener(type, fn, opts));
    }

    /* ---------------- variants ---------------- */
    bindVariants() {
      if (!this.picker || !this.variants) return;
      this.listen(this.picker, 'change', () => this.onVariantChange());
      this.markAvailability();
    }

    /* Sizes the website offers itself (S35): one Shopify variant, every size on offer, the picked size
       rides on the bag line as a line item property. A size other than the stocked one is ordered in. */
    bindThemeSizes() {
      const fs = this.querySelector('[data-theme-sizes]');
      if (!fs) return;
      const note = this.querySelector('[data-ordered-in]');
      this.listen(fs, 'change', () => {
        const input = fs.querySelector('input:checked');
        if (!input) return;
        fs.querySelector('[data-option-selected]').textContent = input.value;
        if (note) note.hidden = 'stocked' in input.dataset ? fs.dataset.stockedOk === 'true' : fs.dataset.canOrder !== 'true';
      });
    }

    selectedOptions() {
      return Array.from(this.picker.querySelectorAll('fieldset')).map((fs) => fs.querySelector('input:checked')?.value ?? null);
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }

    /* Chips for values that give no available variant (with the other choices as they are)
       are marked unavailable but stay selectable. */
    markAvailability() {
      const current = this.selectedOptions();
      this.picker.querySelectorAll('fieldset').forEach((fs, i) => {
        fs.querySelectorAll('input').forEach((input) => {
          const probe = current.slice();
          probe[i] = input.value;
          const v = this.findVariant(probe);
          const chip = input.nextElementSibling;
          chip.classList.toggle('is-unavailable', !v || !v.available);
          /* Say it too, not only show it (WCAG 1.3.1). */
          const state = chip.querySelector('[data-option-state]');
          if (state) state.textContent = !v ? `, ${this.cfg.unavailable}` : !v.available ? `, ${this.cfg.soldOut}` : '';
        });
        const sel = fs.querySelector('[data-option-selected]');
        if (sel) sel.textContent = current[i] || '';
      });
    }

    async onVariantChange() {
      this.markAvailability();
      /* An add-to-bag error was about the previous selection. */
      const err = this.querySelector('[data-form-error]');
      if (err) {
        err.hidden = true;
        err.textContent = '';
      }
      /* Only the latest selection may paint the page: cancel the previous section fetch. */
      this.request?.abort();
      this.request = null;
      const variant = this.findVariant(this.selectedOptions());
      const idInput = this.querySelector('[data-variant-id]');
      const addBtns = this.querySelectorAll('[data-add-to-bag]');
      const dyn = this.querySelector('[data-dynamic-checkout]');

      if (!variant) {
        addBtns.forEach((b) => {
          b.disabled = true;
          b.querySelector('[data-add-label]').textContent = this.cfg.unavailable;
        });
        dyn?.classList.add('is-hidden');
        return;
      }

      /* Price guard: a price of 0 (cents) means "not priced yet", never buyable. */
      const priced = variant.price > 0;
      const buyable = priced && variant.available;
      if (idInput) idInput.value = variant.id;
      addBtns.forEach((b) => {
        b.disabled = !buyable;
        b.querySelector('[data-add-label]').textContent = !priced ? this.cfg.priceTbc : variant.available ? this.cfg.addToBag : this.cfg.soldOut;
      });
      dyn?.classList.toggle('is-hidden', !buyable);

      const url = `${this.cfg.url}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);

      if (variant.featured_media) this.scrollToMedia(variant.featured_media.id);

      /* Re-render price, instalment line, restock / ordered-in note, spec table and carat row for this variant. */
      const request = new AbortController();
      this.request = request;
      try {
        const res = await fetch(`${url}&section_id=${this.cfg.sectionId}`, { signal: request.signal });
        const text = await res.text();
        if (this.request !== request) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        ['[data-pdp-price]', '[data-pdp-payment]', '[data-pdp-restock]', '[data-pdp-specs]', '[data-pdp-materials]', '[data-pdp-sticky-price]'].forEach((sel) => {
          const fresh = html.querySelector(sel);
          const mine = this.querySelector(sel);
          if (!fresh || !mine) return;
          /* Keep an accordion the visitor opened open, and keep focus inside it. */
          const wasOpen = mine.querySelector('details')?.open;
          const hadFocus = mine.contains(document.activeElement);
          mine.innerHTML = fresh.innerHTML;
          const details = mine.querySelector('details');
          if (details && wasOpen !== undefined) details.open = wasOpen;
          if (hadFocus) mine.querySelector('summary')?.focus();
        });
      } catch {
        /* aborted by a newer selection, or offline: the page keeps the old price until the next load */
      } finally {
        if (this.request === request) this.request = null;
      }
    }

    /* ---------------- gallery ---------------- */
    bindGallery() {
      if (!this.track) return;
      const dots = Array.from(this.querySelectorAll('[data-media-dot]'));
      const items = Array.from(this.track.querySelectorAll('[data-media-index]'));
      if (dots.length) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && e.intersectionRatio >= 0.6) {
                const idx = Number(e.target.dataset.mediaIndex);
                /* Plain buttons, all in the tab order; aria-current marks the photo in view. */
                dots.forEach((d, k) => {
                  if (k === idx - 1) d.setAttribute('aria-current', 'true');
                  else d.removeAttribute('aria-current');
                });
              }
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        items.forEach((el) => this.io.observe(el));
        dots.forEach((dot) => {
          this.listen(dot, 'click', () => {
            const item = items[Number(dot.dataset.mediaDot) - 1];
            item?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'nearest', inline: 'start' });
          });
        });
      }
    }

    scrollToMedia(id) {
      const item = this.track?.querySelector(`[data-media-id="${id}"]`);
      if (!item) return;
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      item.scrollIntoView({ behavior: this.scrollBehavior(), block: desktop ? 'start' : 'nearest', inline: 'start' });
    }

    /* ---------------- product videos ---------------- */
    /* Rendered without autoplay and with controls (pause). Played only for visitors who allow
       motion, and only while in view. */
    bindVideos() {
      const videos = Array.from(this.querySelectorAll('.pdp-media__video')).filter((v) => v.tagName === 'VIDEO');
      if (!videos.length) return;
      const motion = window.matchMedia('(prefers-reduced-motion: no-preference)');
      const stopped = new WeakSet(); /* paused by the visitor: never restarted by us */
      videos.forEach((v) => {
        this.listen(v, 'pause', () => {
          if (!v.dataset.autoPaused) stopped.add(v);
          delete v.dataset.autoPaused;
        });
        this.listen(v, 'play', () => stopped.delete(v));
      });
      this.videoIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            const v = e.target;
            if (e.isIntersecting && motion.matches && !stopped.has(v)) {
              v.play().catch(() => {});
            } else if (!e.isIntersecting && !v.paused) {
              v.dataset.autoPaused = '1';
              v.pause();
            }
          });
        },
        { threshold: 0.5 }
      );
      videos.forEach((v) => this.videoIo.observe(v));
    }

    /* ---------------- lightbox ---------------- */
    bindLightbox() {
      const box = this.querySelector('[data-lightbox]');
      if (!box) return;
      this.listen(this, 'click', (e) => {
        const trigger = e.target.closest('[data-lightbox-open]');
        if (!trigger) return;
        const id = trigger.dataset.lightboxOpen;
        box.querySelectorAll('[data-lightbox-item]').forEach((item) => {
          item.hidden = item.dataset.lightboxItem !== id;
          item.classList.remove('is-zoomed');
        });
        box.showModal();
      });
      this.listen(box, 'click', (e) => {
        const item = e.target.closest('[data-lightbox-item]');
        if (item && e.target.tagName === 'IMG') item.classList.toggle('is-zoomed');
      });
    }

    /* ---------------- sticky bar ---------------- */
    bindSticky() {
      const bar = this.querySelector('[data-pdp-sticky]');
      const main = this.querySelector('.buy-buttons__add');
      if (!bar || !main) return;
      /* Show the bar only once the main button has scrolled up out of view. */
      this.stickyIo = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          bar.hidden = e.isIntersecting || e.boundingClientRect.top > 0;
          /* Keep keyboard focus clear of the fixed bar (WCAG 2.4.11): while it shows, the page
             reserves its height at the bottom of the scrollport. */
          const h = bar.hidden ? 0 : bar.offsetHeight;
          document.documentElement.style.scrollPaddingBottom = h ? `calc(${h}px + var(--s-4))` : '';
        },
        { threshold: 0 }
      );
      this.stickyIo.observe(main);
    }

    /* ---------------- sticky info column (desktop) ---------------- */
    /* A column taller than the window would pin its top and hide its end until the photos
       run out. Pinning it by its bottom edge instead hides its top, and keyboard focus can
       land there out of sight, so a column that does not fit simply scrolls with the page. */
    bindStickyColumn() {
      const col = this.querySelector('.pdp__sticky');
      if (!col || !('ResizeObserver' in window)) return;
      const fit = () => {
        const r = col.getBoundingClientRect();
        const before = r.top;
        const onScreen = r.bottom > 0 && r.top < window.innerHeight;
        col.style.position = '';
        const top = parseFloat(getComputedStyle(col).top);
        if (Number.isNaN(top)) return; /* not sticky at this width */
        if (col.offsetHeight + top > window.innerHeight) {
          col.style.position = 'static';
          /* A column that stops sticking mid-scroll (an accordion opened) would jump back to the
             top of the grid, far above the window. Scroll by the same amount so it, and the
             focused control, stay where they were on screen. Only when it was on screen: a resize,
             zoom or late font load while the visitor reads further down must not move the page. */
          const shift = col.getBoundingClientRect().top - before;
          if (shift && onScreen) window.scrollBy({ top: shift, behavior: 'instant' });
        }
      };
      this.stickyRo = new ResizeObserver(fit);
      this.stickyRo.observe(col);
      this.listen(window, 'resize', fit);
    }

    /* ---------------- share ---------------- */
    bindShare() {
      const btn = this.querySelector('[data-share]');
      if (!btn) return;
      const status = this.querySelector('[data-share-status]');
      const label = btn.textContent;
      let timer;
      this.listen(btn, 'click', async () => {
        const data = { title: btn.dataset.shareTitle, url: btn.dataset.shareUrl };
        if (navigator.share) {
          try {
            await navigator.share(data);
          } catch {
            /* dismissed */
          }
        } else if (navigator.clipboard) {
          let msg;
          try {
            await navigator.clipboard.writeText(data.url);
            msg = btn.dataset.copied;
          } catch {
            msg = btn.dataset.copyFailed;
          }
          if (!msg) return;
          /* Shown on the button and announced through the polite status region (WCAG 4.1.3). */
          btn.textContent = msg;
          if (status) status.textContent = msg;
          clearTimeout(timer);
          timer = setTimeout(() => {
            btn.textContent = label;
            if (status) status.textContent = '';
          }, 1600);
        }
      });
      this.cleanups.push(() => clearTimeout(timer));
    }
  }

  (window.antarti?.define || ((n, c) => customElements.get(n) || customElements.define(n, c)))('antarti-product', AntartiProduct);
})();
